import * as _ from 'lodash';

import { getFormValues as getCommitmentFormValues } from '../../[implementationId]/facilities/[facilityId]/commitment-mechanics/[commitmentId]/dataMappings/getFormValues';
import { getUnusedFeeFormValues } from '../../[implementationId]/facilities/[facilityId]/expense-mechanics/[expenseId]/dataMappings/getFormValues';
import { getFormValues as getFacilityFormValues } from '../../[implementationId]/facilities/[facilityId]/facility-mechanics/dataMappings/getFormValues';
import { getFormValues as getPartiesFormValues } from '../../[implementationId]/facilities/[facilityId]/parties/dataMappings/getFormValues';
import { PROPERTY_ITEMS } from '~/core/routes/settings/implementations/[implementationId]/facilities/[facilityId]/facility-mechanics/FacilityMechanics';
import { PARTIES } from '~/core/routes/settings/implementations/[implementationId]/facilities/[facilityId]/parties/Parties';
import {
  getBenchmarkFormValues,
  getFixedFormValues,
} from '~/core/routes/settings/implementations/[implementationId]/facilities/[facilityId]/pricing-options/[pricingOptionId]/dataMappings/getFormValues';
import { CreditAgreementStore } from '~/core/routes/settings/implementations/common/CreditAgreementProvider/store';

export const getFacilityItems = (store: CreditAgreementStore) => {
  const creditAgreement = store.creditAgreement;

  if (store.showOnlyChangesInNav && !store.isEditing) {
    const previousCreditAgreement = store.previousCreditAgreement;

    if (!creditAgreement || !previousCreditAgreement) return [];

    const currentValues = getFacilityFormValues(creditAgreement);
    const previousValues = getFacilityFormValues(previousCreditAgreement);

    return PROPERTY_ITEMS.filter(
      (item) => currentValues[item.name] !== previousValues[item.name],
    ).map(({ name, label }) => ({
      anchor: name,
      label,
    }));
  }

  if (creditAgreement === null) {
    return [];
  }

  const currentValues = getFacilityFormValues(creditAgreement);

  return PROPERTY_ITEMS.filter(
    (item) => currentValues[item.name] !== undefined,
  ).map(({ name, label }) => ({
    anchor: name,
    label,
  }));
};

export const getCommitments = (store: CreditAgreementStore) => {
  if (store.showOnlyChangesInNav && !store.isEditing) {
    const currentCreditAgreement = store.creditAgreement;
    const previousCreditAgreement = store.previousCreditAgreement;

    if (!currentCreditAgreement || !previousCreditAgreement) return [];

    const currentCommitments =
      currentCreditAgreement.loanMechanicsV1?.commitments ?? [];
    const previousCommitments =
      previousCreditAgreement.loanMechanicsV1?.commitments ?? [];

    const uniqueCommitmentKeys = [
      ...new Set([
        ...currentCommitments.map((c) => c.key),
        ...previousCommitments.map((c) => c.key),
      ]),
    ];

    return uniqueCommitmentKeys
      .filter((key) => {
        const currentCommitment = currentCommitments.find((c) => c.key === key);
        const previousCommitment = previousCommitments.find(
          (c) => c.key === key,
        );

        // getCommitmentFormValues will throw if the creditAgreement doesn't contain
        // a commitment with `key`, so we need to make sure it exists first
        const currentFormData = currentCommitment
          ? getCommitmentFormValues(key, currentCreditAgreement)
          : null;
        const previousFormData = previousCommitment
          ? getCommitmentFormValues(key, previousCreditAgreement)
          : null;

        return !_.isEqual(currentFormData, previousFormData);
      })
      .map((key) => {
        const match =
          currentCommitments.find((c) => c.key === key) ??
          previousCommitments.find((c) => c.key === key);

        return {
          id: key,
          label: match?.name ?? key, // key should never show up here - just being type-safe
        };
      });
  }

  return (
    store.creditAgreement?.loanMechanicsV1?.commitments.map((commitment) => ({
      id: commitment.key,
      label: commitment.name,
    })) ?? []
  );
};

export const getParties = (store: CreditAgreementStore) => {
  if (store.showOnlyChangesInNav && !store.isEditing) {
    const currentCreditAgreement = store.creditAgreement;
    const previousCreditAgreement = store.previousCreditAgreement;

    if (!currentCreditAgreement || !previousCreditAgreement) return [];

    const currentValues = getPartiesFormValues(currentCreditAgreement);
    const previousValues = getPartiesFormValues(previousCreditAgreement);

    return PARTIES.filter((party) => {
      const current = currentValues[party.anchor];
      const previous = previousValues[party.anchor];

      return !_.isEqual(current, previous);
    });
  }

  return PARTIES.filter(
    (party) =>
      (store.creditAgreement?.facilityMechanics.parties[party.anchor]?.length ??
        0) > 0,
  );
};

export const getExpenses = (store: CreditAgreementStore) => {
  if (store.showOnlyChangesInNav && !store.isEditing) {
    const currentAccounts = store.creditAgreement?.liabilityAccounts ?? [];
    const previousAccounts =
      store.previousCreditAgreement?.liabilityAccounts ?? [];

    const allUniqueSlugs = [
      ...new Set([
        ...currentAccounts.map((a) => a.counterpartySlug),
        ...previousAccounts.map((a) => a.counterpartySlug),
      ]),
    ];

    return allUniqueSlugs
      .filter((slug) => {
        const current = getUnusedFeeFormValues(slug, currentAccounts);
        const previous = getUnusedFeeFormValues(slug, previousAccounts);

        return !_.isEqual(current, previous);
      })
      .map((slug) => {
        const account =
          currentAccounts.find((a) => a.counterpartySlug === slug) ??
          previousAccounts.find((a) => a.counterpartySlug === slug);

        return {
          counterpartySlug: slug,
          label: account?.name ?? 'Unused Fee',
        };
      });
  }

  return (
    store.creditAgreement?.liabilityAccounts?.map((account) => ({
      counterpartySlug: account.counterpartySlug,
      label: account.name,
    })) ?? []
  );
};

export const getPricingOptions = (store: CreditAgreementStore) => {
  if (store.showOnlyChangesInNav && !store.isEditing) {
    const currentAgreement = store.creditAgreement;
    const previousAgreement = store.previousCreditAgreement;

    if (!currentAgreement || !previousAgreement) return [];

    const currentOptions =
      currentAgreement.loanMechanicsV1?.pricingOptions ?? [];
    const previousOptions =
      previousAgreement.loanMechanicsV1?.pricingOptions ?? [];

    const allUniquePricingOptionKeys = [
      ...new Set([
        ...currentOptions.map((o) => o.key),
        ...previousOptions.map((o) => o.key),
      ]),
    ];

    return allUniquePricingOptionKeys
      .filter((key) => {
        const currentOption = currentOptions.find((o) => o.key === key);
        const previousOption = previousOptions.find((o) => o.key === key);

        const currentType = currentOption?.type;
        const previousType = previousOption?.type;

        // Bail if types don't match. This should never happen, since pricing option types are fixed at creation,
        // but getFormValues can throw if theres a type mismatch so we're just being safe here.
        if (currentType && previousType && currentType !== previousType)
          return false;

        const getFormValues =
          currentType === 'fixed' || previousType === 'fixed'
            ? getFixedFormValues
            : getBenchmarkFormValues;

        const currentForm = currentOption
          ? getFormValues(currentAgreement, key)
          : null;
        const previousForm = previousOption
          ? getFormValues(previousAgreement, key)
          : null;

        return !_.isEqual(currentForm, previousForm);
      })
      .map((key) => {
        const match =
          currentOptions.find((o) => o.key === key) ??
          previousOptions.find((o) => o.key === key);

        return {
          key,
          label: match?.name ?? key,
        };
      });
  }

  return (
    store.creditAgreement?.loanMechanicsV1?.pricingOptions?.map(
      (pricingOption) => ({
        key: pricingOption.key,
        label: pricingOption.name,
      }),
    ) ?? []
  );
};
